import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card, Badge, Toggle, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import styles from './Portals.module.css';

interface QueueItem {
  id: string;
  property_id: string;
  property_name: string;
  portal: string;
  status: string;
  error_message: string | null;
  submitted_at: string | null;
}

interface Property {
  id: string;
  name: string;
  portal_suumo?: string;
  portal_homes?: string;
  portal_athome?: string;
}

const portalDefs = [
  { key: 'suumo', name: 'SUUMO', plan: 'スタンダード掲載' },
  { key: 'homes', name: "LIFULL HOME'S", plan: 'プレミアム掲載' },
  { key: 'athome', name: 'athome', plan: 'スタンダード掲載' },
];

export function Portals() {
  const navigate = useNavigate();
  const { data: queue, refetch: refetchQueue } = useApi<QueueItem[]>('/tenant/portals/queue');
  const { data: properties, loading, refetch: refetchProps } = useApi<Property[]>('/tenant/properties');
  const { data: portalStatsData } = useApi<{ source: string; cnt: string }[]>('/tenant/reports/portal-stats');
  const { data: config } = useApi<{ plan_name: string }>('/tenant/config');
  const planName = config?.plan_name ?? 'Standard';
  const isStandardOrBelow = ['Trial', 'Standard'].includes(planName);
  const [autoSubmit, setAutoSubmit] = useState<Record<string, boolean>>({
    suumo: true, homes: true, athome: true,
  });
  const [errorDetail, setErrorDetail] = useState<QueueItem | null>(null);
  const [settingsTarget, setSettingsTarget] = useState<Property | null>(null);
  const [portalSettings, setPortalSettings] = useState({ suumo: false, homes: false, athome: false });

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  const portalCounts: Record<string, number> = { suumo: 0, homes: 0, athome: 0 };
  const portalInquiries: Record<string, number> = { suumo: 0, homes: 0, athome: 0 };
  (portalStatsData ?? []).forEach((s) => {
    if (s.source === 'suumo') portalInquiries.suumo = Number(s.cnt);
    if (s.source === 'homes') portalInquiries.homes = Number(s.cnt);
    if (s.source === 'athome') portalInquiries.athome = Number(s.cnt);
  });
  (properties ?? []).forEach((p) => {
    if (p.portal_suumo === 'submitted') portalCounts.suumo++;
    if (p.portal_homes === 'submitted') portalCounts.homes++;
    if (p.portal_athome === 'submitted') portalCounts.athome++;
  });

  const errorProps = new Set<string>();
  (queue ?? []).forEach((q) => { if (q.status === 'error') errorProps.add(q.property_name); });

  const handleBulkResubmit = async () => {
    try {
      await api('/tenant/portals/resubmit-all', { method: 'POST' });
      toast('全件再入稿を開始しました', 'success');
      refetchQueue();
    } catch {
      toast('再入稿に失敗しました', 'error');
    }
  };

  const handleOpenSettings = (p: Property) => {
    setSettingsTarget(p);
    setPortalSettings({
      suumo: p.portal_suumo === 'submitted',
      homes: p.portal_homes === 'submitted',
      athome: p.portal_athome === 'submitted',
    });
  };

  const handleSaveSettings = async () => {
    if (!settingsTarget) return;
    try {
      await api(`/tenant/properties/${settingsTarget.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          portal_suumo: portalSettings.suumo ? 'submitted' : 'none',
          portal_homes: portalSettings.homes ? 'submitted' : 'none',
          portal_athome: portalSettings.athome ? 'submitted' : 'none',
        }),
      });
      toast('入稿設定を保存しました', 'success');
      setSettingsTarget(null);
      refetchProps();
    } catch {
      toast('保存に失敗しました', 'error');
    }
  };

  return (
    <div className={styles.page}>
      {/* Portal cards */}
      <div className={styles.portalGrid}>
        {portalDefs.map((portal) => (
          <div key={portal.key} className={styles.portalCard}>
            <div className={styles.portalCardHeader}>
              <div>
                <div className={styles.portalCardName}>{portal.name}</div>
                <div className={styles.portalCardPlan}>{portal.plan}</div>
              </div>
            </div>
            <div className={styles.portalCardStats}>
              <div className={styles.portalStat}>
                <div className={styles.portalStatValue}>{portalCounts[portal.key]}</div>
                <div className={styles.portalStatLabel}>掲載中</div>
              </div>
              <div className={styles.portalStat}>
                <div className={styles.portalStatValue}>{portalInquiries[portal.key]}</div>
                <div className={styles.portalStatLabel}>今月問合せ</div>
              </div>
            </div>
            <div className={styles.portalCardFooter}>
              <Toggle
                checked={autoSubmit[portal.key]}
                onChange={(v) => setAutoSubmit((prev) => ({ ...prev, [portal.key]: v }))}
              />
              <span>自動入稿 {autoSubmit[portal.key] ? 'ON' : 'OFF'}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Plan limit alert - Standard以下のみ表示 */}
      {isStandardOrBelow && (
        <div className={styles.alertBanner}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>📌</span>
          <span className={styles.alertText}>
            現在の <strong>{planName}プラン</strong> では3媒体まで対応しています。CHINTAI・Yahoo不動産・楽天不動産への入稿は <strong>Professionalプランへのアップグレード</strong> またはアドオン契約が必要です。
          </span>
          <button className={styles.alertBtn} onClick={() => toast('アップグレードは住縁 Mirai EMS管理コンソールから申請してください', 'info')}>アップグレード申請</button>
        </div>
      )}

      {/* Property portal status */}
      <Card title="物件別入稿ステータス" actions={
        <button className={styles.bulkBtn} onClick={handleBulkResubmit}>⚡ 全件再入稿</button>
      }>
        <div className={styles.propList}>
          {(properties ?? []).map((p) => {
            const hasError = errorProps.has(p.name);
            return (
              <div key={p.id} className={styles.propItem}>
                <span className={styles.propName}>{p.name}</span>
                <div className={styles.propBadges}>
                  <span className={`${styles.propBadge} ${p.portal_suumo === 'submitted' ? styles.ok : styles.ng}`}>
                    {p.portal_suumo === 'submitted' ? '✓' : '✕'} SUUMO
                  </span>
                  <span className={`${styles.propBadge} ${p.portal_homes === 'submitted' ? styles.ok : styles.ng}`}>
                    {p.portal_homes === 'submitted' ? '✓' : '✕'} HOME'S
                  </span>
                  <span className={`${styles.propBadge} ${p.portal_athome === 'submitted' ? styles.ok : styles.ng}`}>
                    {p.portal_athome === 'submitted' ? '✓' : '✕'} athome
                  </span>
                </div>
                {hasError ? (
                  <button className={`${styles.propAction} ${styles.error}`} onClick={() => {
                    const errorItem = (queue ?? []).find(q => q.property_name === p.name && q.status === 'error');
                    if (errorItem) setErrorDetail(errorItem);
                  }}>エラー詳細</button>
                ) : (
                  <button className={styles.propAction} onClick={() => handleOpenSettings(p)}>設定</button>
                )}
              </div>
            );
          })}
          {(!properties || properties.length === 0) && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink3)' }}>物件データはありません</div>
          )}
        </div>
      </Card>

      {/* Error detail modal */}
      <Modal open={!!errorDetail} onClose={() => setErrorDetail(null)} title="ポータル入稿エラー詳細">
        {errorDetail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className={styles.modalRow}><span className={styles.modalLabel}>物件名</span><span>{errorDetail.property_name}</span></div>
            <div className={styles.modalRow}><span className={styles.modalLabel}>ポータル</span><span>{errorDetail.portal}</span></div>
            <div className={styles.modalRow}><span className={styles.modalLabel}>送信日時</span><span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{errorDetail.submitted_at ? new Date(errorDetail.submitted_at).toLocaleString('ja-JP') : '—'}</span></div>
            <div style={{ marginTop: 8 }}>
              <div className={styles.modalLabel} style={{ marginBottom: 4 }}>エラーメッセージ</div>
              <div className={styles.errorBox}>{errorDetail.error_message || 'エラー詳細情報なし'}</div>
            </div>
          </div>
        )}
      </Modal>

      {/* Portal settings modal */}
      <Modal open={!!settingsTarget} onClose={() => setSettingsTarget(null)} title={`入稿設定 — ${settingsTarget?.name ?? ''}`} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.propAction} onClick={() => setSettingsTarget(null)}>キャンセル</button>
          <button className={styles.alertBtn} onClick={handleSaveSettings}>保存</button>
        </div>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {portalDefs.map(pd => (
            <label key={pd.key} className={styles.settingsRow}>
              <Toggle
                checked={portalSettings[pd.key as keyof typeof portalSettings]}
                onChange={(v) => setPortalSettings(prev => ({ ...prev, [pd.key]: v }))}
              />
              <span className={styles.settingsName}>{pd.name}</span>
              <span className={styles.settingsPlan}>{pd.plan}</span>
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}
